import { useDocumentTitle } from '../app/useDocumentTitle';
import type { BoardListFilter, BoardSummaryDto, TemplateId } from '@miroom/shared';
import { Clock, FileUp, Folder, Plus, Search, Star, Trash2, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardActions, useDashboard } from '../../application/stores/dashboardStore';
import { toast } from '../../application/stores/toastStore';
import { useContainer } from '../../di/ContainerProvider';
import { Button, IconButton } from '../components/Button';
import { Logo } from '../components/Logo';
import { Modal } from '../components/Modal';
import { Segmented } from '../components/Segmented';
import { TextField } from '../components/TextField';
import { UserMenu } from '../components/UserMenu';
import { errorMessage, useT } from '../i18n/i18n';
import { BoardCard, type BoardCardActions } from './dashboard/BoardCard';
import styles from './dashboard/Dashboard.module.css';
import { TemplateGallery, TemplatesModal } from './dashboard/TemplatesModal';

const TABS: { id: BoardListFilter; icon: typeof Clock }[] = [
  { id: 'recent', icon: Clock },
  { id: 'mine', icon: Folder },
  { id: 'shared', icon: Users },
  { id: 'starred', icon: Star },
  { id: 'trash', icon: Trash2 },
];

const QUICK: TemplateId[] = ['blank', 'retro', 'kanban', 'mindmap', 'flowchart', 'brainstorm'];

export function DashboardPage() {
  const t = useT();
  useDocumentTitle(t('dashboard.title'));
  const navigate = useNavigate();
  const { boardsApi } = useContainer();
  const actions = useMemo(() => dashboardActions(boardsApi), [boardsApi]);
  const { filter, search, sort, boards, status, error, setFilter, setSearch, setSort } =
    useDashboard();
  const [query, setQuery] = useState(search);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [creating, setCreating] = useState<TemplateId | null>(null);
  const [renaming, setRenaming] = useState<BoardSummaryDto | null>(null);
  const [destroying, setDestroying] = useState<BoardSummaryDto | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void actions.load();
  }, [actions, filter, search, sort]);

  // Debounce typing into the search box.
  useEffect(() => {
    const id = setTimeout(() => setSearch(query), 250);
    return () => clearTimeout(id);
  }, [query, setSearch]);

  const fail = (e: unknown) => toast.error(errorMessage(t, e));

  const create = async (template: TemplateId) => {
    setCreating(template);
    try {
      const board = await actions.create(
        template,
        template === 'blank' ? undefined : t(`templates.${template}.name`),
      );
      navigate(`/b/${board.id}`);
    } catch (e) {
      fail(e);
      setCreating(null);
    }
  };

  const cardActions: BoardCardActions = {
    onStar: (b) => actions.toggleStar(b).catch(fail),
    onRename: setRenaming,
    onDuplicate: async (b) => {
      try {
        const copy = await actions.duplicate(b);
        toast.success(t('dashboard.duplicated', { title: b.title }));
        if (filter === 'mine' || filter === 'recent') void actions.load();
        return copy;
      } catch (e) {
        fail(e);
      }
    },
    onRemove: async (b) => {
      try {
        await actions.remove(b);
        if (b.role === 'owner') {
          toast.info(t('dashboard.movedToTrash'), {
            label: t('dashboard.undo'),
            run: () => actions.restore(b).catch(fail),
          });
        } else toast.info(t('dashboard.left'));
      } catch (e) {
        fail(e);
      }
    },
    onRestore: async (b) => {
      try {
        await actions.restore(b);
        toast.success(t('dashboard.restored'));
      } catch (e) {
        fail(e);
      }
    },
    onDestroy: setDestroying,
  };

  const importFile = async (file: File) => {
    try {
      const snapshot: unknown = JSON.parse(await file.text());
      const board = await actions.importFile(
        snapshot,
        file.name.replace(/(\.miroom)?\.json$/i, '').slice(0, 120) || undefined,
      );
      navigate(`/b/${board.id}`);
    } catch (e) {
      if (e instanceof SyntaxError || (e as { code?: string }).code === 'validation')
        toast.error(t('dashboard.importFailed'));
      else fail(e);
    }
  };

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <Link to="/boards" className={styles.logoLink} aria-label="miroom">
          <Logo />
        </Link>
        <div className={styles.search} role="search">
          <Search size={18} className={styles.searchIcon} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('dashboard.searchPlaceholder')}
            aria-label={t('dashboard.searchPlaceholder')}
          />
        </div>
        <div className={styles.headerActions}>
          <IconButton label={t('dashboard.import')} onClick={() => fileRef.current?.click()}>
            <FileUp size={18} />
          </IconButton>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void importFile(f);
            }}
          />
          <Button icon={<Plus size={18} />} onClick={() => setTemplatesOpen(true)}>
            <span className={styles.newLabel}>{t('dashboard.newBoard')}</span>
          </Button>
          <UserMenu />
        </div>
      </header>

      <nav className={styles.nav} role="tablist" aria-label={t('dashboard.title')}>
        {TABS.map(({ id, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={filter === id}
            className={styles.tab}
            onClick={() => setFilter(id)}
          >
            <Icon size={18} aria-hidden="true" />
            {t(`dashboard.tabs.${id}`)}
          </button>
        ))}
      </nav>

      <main className={styles.main}>
        {filter !== 'trash' && !search && (
          <section aria-labelledby="quick-start">
            <div className={styles.sectionHead}>
              <h2 id="quick-start" className={styles.h2}>
                {t('dashboard.quickStart')}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setTemplatesOpen(true)}>
                {t('dashboard.allTemplates')}
              </Button>
            </div>
            <TemplateGallery ids={QUICK} busy={creating} onPick={create} compact />
          </section>
        )}

        <section aria-labelledby="boards-title" role="tabpanel">
          <div className={styles.sectionHead}>
            <h1 id="boards-title" className={styles.h1}>
              {t(`dashboard.tabs.${filter}`)}
            </h1>
            {filter === 'trash' ? (
              <span className={styles.hint}>{t('dashboard.trashHint')}</span>
            ) : (
              <Segmented
                label={t('dashboard.sort')}
                value={sort}
                onChange={setSort}
                options={[
                  { value: 'date', label: t('dashboard.sortDate') },
                  { value: 'name', label: t('dashboard.sortName') },
                ]}
              />
            )}
          </div>
          <BoardList
            status={status}
            boards={boards}
            error={error}
            filter={filter}
            search={search}
            actions={cardActions}
            onRetry={() => void actions.load()}
            onCreate={() => create('blank')}
          />
        </section>
      </main>

      {templatesOpen && (
        <TemplatesModal onClose={() => setTemplatesOpen(false)} onCreate={create} />
      )}
      {renaming && (
        <RenameModal
          board={renaming}
          onClose={() => setRenaming(null)}
          onSave={async (title) => {
            await actions.rename(renaming, title);
            setRenaming(null);
          }}
        />
      )}
      {destroying && (
        <Modal title={t('dashboard.deleteForeverTitle')} onClose={() => setDestroying(null)}>
          <p>{t('dashboard.deleteForeverText', { title: destroying.title })}</p>
          <div className={styles.confirmActions}>
            <Button variant="secondary" onClick={() => setDestroying(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                const b = destroying;
                setDestroying(null);
                await actions.destroy(b).catch(fail);
              }}
            >
              {t('dashboard.deleteForever')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function BoardList({
  status,
  boards,
  error,
  filter,
  search,
  actions,
  onRetry,
  onCreate,
}: {
  status: string;
  boards: BoardSummaryDto[];
  error: unknown;
  filter: BoardListFilter;
  search: string;
  actions: BoardCardActions;
  onRetry(): void;
  onCreate(): void;
}) {
  const t = useT();
  if (status === 'error') {
    return (
      <div className={styles.error} role="alert">
        <strong>{t('dashboard.loadError')}</strong>
        <span>{errorMessage(t, error)}</span>
        <Button variant="secondary" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }
  if ((status === 'loading' || status === 'idle') && boards.length === 0) {
    return (
      <ul className={styles.grid} aria-busy="true" aria-label={t('common.loading')}>
        {Array.from({ length: 6 }, (_, i) => (
          <li key={i} className={styles.skeleton} />
        ))}
      </ul>
    );
  }
  if (boards.length === 0) {
    return (
      <div className={styles.empty}>
        <span className={styles.emptyArt} aria-hidden="true">
          {search ? '🔍' : filter === 'trash' ? '🗑️' : filter === 'starred' ? '⭐' : '🗒️'}
        </span>
        <p>
          {search ? t('dashboard.empty.search', { q: search }) : t(`dashboard.empty.${filter}`)}
        </p>
        {!search && (filter === 'recent' || filter === 'mine') && (
          <>
            <p className={styles.hint}>{t('dashboard.empty.cta')}</p>
            <Button icon={<Plus size={18} />} onClick={onCreate}>
              {t('dashboard.newBoard')}
            </Button>
          </>
        )}
      </div>
    );
  }
  return (
    <ul className={styles.grid} aria-busy={status === 'loading'}>
      {boards.map((b) => (
        <li key={b.id}>
          <BoardCard board={b} actions={actions} />
        </li>
      ))}
    </ul>
  );
}

function RenameModal({
  board,
  onClose,
  onSave,
}: {
  board: BoardSummaryDto;
  onClose(): void;
  onSave(title: string): Promise<void>;
}) {
  const t = useT();
  const [title, setTitle] = useState(board.title);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave(title.trim());
    } catch (err) {
      setError(errorMessage(t, err));
      setSaving(false);
    }
  };
  return (
    <Modal title={t('dashboard.renameTitle')} onClose={onClose}>
      <form onSubmit={submit}>
        <TextField
          label={t('dashboard.boardName')}
          value={title}
          maxLength={120}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={(e) => e.target.select()}
          error={error}
          data-autofocus
        />
        <div className={styles.confirmActions}>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={saving} disabled={!title.trim()}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
